import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { getDocEntityChildTypes } from "./docEntityConstraintTools";

// Cache for memoizing results of collectUniqueChildTypes
const childTypesCache = new Map<DocEntityType, DocEntityType[]>();

/**
 * Recursively collects all unique child types for a given DocEntityType or array of DocEntityTypes.
 * This function uses getDocEntityChildTypes() to traverse the docEntityTypeToChildTypes
 * mapping (from the auto-generated docEntityTypeToForeignKeyRelations.ts file) to find all
 * child types associated with the given entity type(s), including nested children.
 * Results are memoized per individual type for performance.
 *
 * @param entityTypes - A single DocEntityType or an array of DocEntityTypes to collect child types for
 * @param options.includeRootTypes - If true, includes the input type(s) in the result (default: false)
 * @returns An array of unique DocEntityType values representing all child types
 */

interface CollectUniqueChildTypesOptions {
  includeRootTypes?: boolean;
}

export function collectUniqueChildTypes(
  entityTypes: DocEntityType | DocEntityType[],
  options: CollectUniqueChildTypesOptions = {},
): DocEntityType[] {
  const { includeRootTypes = false } = options;
  const typesArray = Array.isArray(entityTypes) ? entityTypes : [entityTypes];

  // For a single type, use the optimized cached path
  if (typesArray.length === 1) {
    const childTypes = collectUniqueChildTypesForSingleType(typesArray[0]);
    if (includeRootTypes) {
      return [typesArray[0], ...childTypes];
    }
    return childTypes;
  }

  // For multiple types, collect and combine results
  const combinedResult = new Set<DocEntityType>(includeRootTypes ? typesArray : []);
  const rootTypes = new Set(typesArray);

  for (const entityType of typesArray) {
    const childTypes = collectUniqueChildTypesForSingleType(entityType);
    for (const childType of childTypes) {
      // Exclude any child types that are also root types (unless includeRootTypes is true)
      if (includeRootTypes || !rootTypes.has(childType)) {
        combinedResult.add(childType);
      }
    }
  }

  return Array.from(combinedResult);
}

/**
 * Internal helper that collects child types for a single entity type with caching.
 * Always returns child types without the root type - the caller handles includeRootTypes.
 */
function collectUniqueChildTypesForSingleType(entityType: DocEntityType): DocEntityType[] {
  // Check cache first
  const cached = childTypesCache.get(entityType);
  if (cached !== undefined) {
    return cached;
  }

  const result = new Set<DocEntityType>();
  const visited = new Set<DocEntityType>();

  // Pre-check if this type has any children to avoid unnecessary recursion
  const immediateChildren = getDocEntityChildTypes(entityType);
  if (immediateChildren.length === 0) {
    childTypesCache.set(entityType, []);
    return [];
  }

  // Helper function to recursively collect child types
  function collectChildTypes(type: DocEntityType, isRoot: boolean = false): void {
    // Skip if we've already visited this type to prevent infinite recursion
    if (visited.has(type)) {
      return;
    }

    // Mark this type as visited
    visited.add(type);

    // Add the current type to the result set, but only if it's not the root type
    if (!isRoot) {
      result.add(type);
    }

    // Get child types for the current type
    const childTypes = getDocEntityChildTypes(type);

    // Check if any of these children are already cached
    for (const childType of childTypes) {
      if (!visited.has(childType)) {
        const cachedChildren = childTypesCache.get(childType);
        if (cachedChildren !== undefined) {
          // Add all cached children to our result and mark them as visited
          cachedChildren.forEach((child) => {
            result.add(child);
            visited.add(child);
          });
        } else {
          // Recursively collect child types if not cached
          collectChildTypes(childType, false);
        }
      }
    }
  }

  // Start collecting from the entity's type, marking it as the root
  collectChildTypes(entityType, true);

  // Convert the Set to an array, cache it, and return
  const resultArray = Array.from(result);
  childTypesCache.set(entityType, resultArray);
  return resultArray;
}

/**
 * Checks if any of the child types of a given entity type match a specified set of types.
 * This function uses collectUniqueChildTypes to get all child types and then checks
 * if any of them match the specified types.
 *
 * @param entityType - The DocEntityType to check child types for
 * @param typesToCheck - A single DocEntityType or an array of DocEntityType values to check for
 * @returns A boolean indicating whether any of the child types match the specified types
 */
export function entityChildTypesContains(
  entityType: DocEntityType,
  typesToCheck: DocEntityType | DocEntityType[],
): boolean {
  // Convert typesToCheck to a Set for O(1) lookups
  const typesSet = new Set(Array.isArray(typesToCheck) ? typesToCheck : [typesToCheck]);

  // Get all child types for the entity type
  const childTypes = collectUniqueChildTypes(entityType);

  // Use Set.has() for O(1) lookups instead of Array.includes()
  return childTypes.some((type) => typesSet.has(type));
}
